import type { PostLanguage, PostMediaKind } from "@/types/post";

export type PublicVocabulary = {
  seo: {
    // Homepage <title> — also reused as og:title / twitter:title and as the name
    // of the feed CollectionPage, so the three stay in sync. Keep it 55-60 chars.
    siteTitle: string;
    // Homepage meta description — also the WebSite/Organization JSON-LD
    // description and the subtitle of the branded OG card. Keep it 150-160 chars.
    siteDescription: string;
    feedNotFoundTitle: string;
    postNotFoundTitle: string;
  };
  status: {
    notFoundEyebrow: string;
    notFoundTitle: string;
    notFoundDescription: string;
    backToFeed: string;
    errorEyebrow: string;
    errorTitle: string;
    errorDescription: string;
    errorDigest: (digest: string) => string;
    retry: string;
    reload: string;
    loadingEyebrow: string;
    loadingTitle: string;
    loadingDescription: string;
  };
  feed: {
    browseEyebrow: string;
    searchAndFilters: string;
    closeFilters: string;
    searchPosts: string;
    searchPlaceholder: string;
    loading: string;
    loadFailed: string;
    loadMoreFailed: string;
    updateFiltersFailed: string;
    search: string;
    clear: string;
    threadsOnly: string;
    videosOnly: string;
    categories: string;
    tags: string;
    resetFilters: string;
    backHome: string;
    filters: string;
    menu: string;
    close: string;
    emptyWithFilters: string;
    emptyWithoutFilters: string;
    loadingMore: string;
    scrollForMore: string;
    comunicatoBadge: string;
    wikiBadge: string;
    threadBadge: (count: number) => string;
    threadPosition: (position: number, total: number) => string;
    followStory: string;
    followStoryLabel: (postLabel: string, threadLabel: string) => string;
    selectedPost: string;
    threadTimeline: (threadLabel: string) => string;
    threadTimelineFailed: string;
    readFullThread: string;
    latestUpdate: string;
    /** Sul telefono, dove la dicitura intera non sta accanto alle fasce. */
    latestUpdateShort: string;
    telexPrompt: string;
    newPosts: (count: number) => string;
    olderPosts: string;
    newerPosts: string;
    openPost: (postLabel: string) => string;
    sharePost: string;
    share: string;
    copyText: string;
    directLink: string;
  };
  taxonomy: {
    tagEyebrow: string;
    countryEyebrow: string;
    tagTitle: (label: string) => string;
    countryTitle: (label: string) => string;
    tagIntro: (label: string) => string;
    countryIntro: (label: string) => string;
    backToFeed: string;
    otherTags: string;
    otherCountries: string;
  };
  post: {
    permalinkThreadHint: string;
    currentlyViewedMessage: string;
    reportTimeline: string;
    threadNumber: (postLabel: string) => string;
    media: string;
    embeds: string;
    latestUpdate: string;
    copy: string;
    share: string;
    threadOfMessages: (count: number) => string;
    singleMessage: string;
    postTitleFallback: string;
    back: string;
    directLink: string;
    linkShort: string;
    olderPost: string;
    newerPost: string;
    backToDispatches: string;
    linkedCount: (count: number) => string;
    otherDispatches: string;
    otherDispatchesHint: string;
    siteArrival: (postLabel: string) => string;
    siteArrivals: (count: number, latestLabel: string) => string;
    openSiteArrival: (postLabel: string) => string;
    dismissSiteArrivals: string;
  };
  media: {
    adultOnlyTitle: string;
    reveal: string;
    revealMedia: (index: number, total: number) => string;
    openMedia: (index: number, total: number) => string;
    images: string;
    maps: string;
    videos: string;
    video: string;
    playVideo: string;
    expandVideo: string;
    audio: string;
    closeViewer: string;
    closeMapViewer: string;
    previousMedia: string;
    nextMedia: string;
  };
  embed: {
    viewOnTikTok: string;
    viewPostOnX: string;
    viewOnProvider: (providerLabel: string) => string;
  };
  share: {
    defaultTitle: string;
    shared: string;
    unavailable: string;
    linkCopied: string;
    failed: string;
    nothingToCopy: string;
    textCopied: string;
    threadCopied: string;
  };
  push: {
    initializingTitle: string;
    initializingMessage: string;
    loadingOneSignalMessage: string;
    unsupportedTitle: string;
    requiresHttps: string;
    iosHomeScreenRequired: string;
    missingServiceWorker: string;
    missingNotificationApi: string;
    missingPushManager: string;
    unsupportedGeneric: string;
    initErrorTitle: string;
    initErrorFallback: string;
    onesignalUnsupported: string;
    subscribedTitle: string;
    subscribedMessage: string;
    permissionDeniedTitle: string;
    permissionDeniedMessage: string;
    enableTitle: string;
    permissionGrantedPendingMessage: string;
    clickToEnableMessage: string;
    alreadyActive: string;
    notReadyReload: string;
    enabled: string;
    disabled: string;
    invisiblePrompt: string;
    optInFailed: string;
    optOutFailed: string;
    menuLabel: string;
    toggleOn: string;
    toggleOff: string;
  };
  telex: {
    checkingLink: string;
    invalidLink: string;
    redirecting: string;
    sendTelex: string;
    logout: string;
    attached: string;
    attachmentReady: (kind: PostMediaKind) => string;
    remove: string;
    composeDialogLabel: string;
    composeEyebrow: string;
    composeTitle: string;
    composeDescription: string;
    close: string;
    textPlaceholder: string;
    addAttachmentTitle: string;
    addAttachmentSubtitle: string;
    cancel: string;
    sending: string;
    attachmentPickerLabel: string;
    attachmentEyebrow: string;
    attachmentTitle: string;
    back: string;
    selectImage: string;
    selectImageHelp: string;
    selectVideo: string;
    selectVideoHelp: string;
    recordAudio: string;
    recordAudioHelp: string;
    recordVideo: string;
    recordVideoHelp: string;
    audioDialogLabel: string;
    cancelRecording: string;
    voiceTelex: string;
    finalizing: string;
    paused: string;
    ready: string;
    recording: string;
    maxDuration: (label: string) => string;
    pauseRecording: string;
    resumeRecording: string;
    startRecording: string;
    stopRecording: string;
    videoDialogLabel: string;
    readyToRecord: string;
    frameShot: string;
    recordingPaused: string;
    holdSteady: string;
    switchCamera: string;
    chooseCameraBeforeStart: string;
    finalizingVideo: string;
    sentDialogLabel: string;
    dismissConfirmation: string;
    sent: string;
    telexSent: string;
    sentDescription: string;
    backNow: string;
    recordingLimitReached: (kind: "audio" | "video") => string;
    invalidSession: string;
    cameraAndMicrophoneAccessFailed: string;
    requestedCameraUnavailable: string;
    liveVideoUnavailable: string;
    switchCameraFailed: string;
    signUploadFailed: string;
    mediaUploadFailed: string;
    writeMessageOrAttachMedia: string;
    messageTooLong: (limit: number) => string;
    sendFailed: string;
    imagesOnly: string;
    prepareImageFailed: string;
    unsupportedVideoFile: string;
    videoTooLarge: (sizeMb: number) => string;
    prepareVideoFailed: string;
    audioRecordingUnsupported: string;
    initAudioRecorderFailed: string;
    audioRecordingFailed: string;
    recordedAudioEmpty: string;
    recordedAudioUnsupported: string;
    finalizeAudioFailed: string;
    microphoneAccessFailed: string;
    liveVideoUnsupportedChooseVideo: string;
    liveVideoUnavailableChooseVideo: string;
    videoRecordingFailed: string;
    recordedVideoEmpty: string;
    recordedVideoUnsupported: string;
    finalizeVideoFailed: string;
  };
};

const ITALIAN_VOCABULARY: PublicVocabulary = {
  seo: {
    siteTitle: "Guerre, crisi e intelligence: i dispacci | SilentPeople",
    siteDescription:
      "Bollettino indipendente del Pensiero Non Allineato (PNA): dispacci brevi su guerre, crisi, intelligence ed economia, paese per paese, aggiornati ogni giorno.",
    feedNotFoundTitle: "Pagina non trovata",
    postNotFoundTitle: "Messaggio non trovato",
  },
  status: {
    notFoundEyebrow: "errore 404 / linea muta",
    notFoundTitle: "Pagina non trovata",
    notFoundDescription:
      "Il messaggio o la pagina che cerchi non esiste piu, oppure l'indirizzo e stato digitato male. Torna al feed per gli ultimi dispacci.",
    backToFeed: "Torna al feed",
    errorEyebrow: "errore 500 / trasmissione interrotta",
    errorTitle: "Qualcosa si e interrotto",
    errorDescription:
      "Non siamo riusciti a caricare questa pagina. Riprova: se il problema resta, riapri il feed tra qualche minuto.",
    errorDigest: (digest) => `Codice errore ${digest}`,
    retry: "Riprova",
    reload: "Ricarica la pagina",
    loadingEyebrow: "ricezione in corso",
    loadingTitle: "Caricamento dispacci...",
    loadingDescription: "Stiamo aprendo la linea con il centro.",
  },
  feed: {
    browseEyebrow: "Esplora",
    searchAndFilters: "Ricerca, paesi e filtri",
    closeFilters: "Chiudi filtri",
    searchPosts: "Cerca messaggi",
    searchPlaceholder: "Testo, notizia o TX",
    loading: "Caricamento...",
    loadFailed: "Caricamento dei messaggi non riuscito. Riprova.",
    loadMoreFailed: "Impossibile caricare altri messaggi in questo momento.",
    updateFiltersFailed: "Impossibile aggiornare i filtri in questo momento.",
    search: "Cerca",
    clear: "Cancella",
    threadsOnly: "Solo thread",
    videosOnly: "Solo video",
    categories: "Paesi",
    tags: "Tag",
    resetFilters: "Azzera filtri",
    backHome: "Torna alla home",
    filters: "Filtri",
    menu: "Menu",
    close: "Chiudi",
    emptyWithFilters: "Nessun messaggio corrisponde alla ricerca o ai filtri attivi.",
    emptyWithoutFilters: "Nessun messaggio.",
    loadingMore: "Caricamento...",
    scrollForMore: "Scorri per altri",
    comunicatoBadge: "Comunicato PNA",
    wikiBadge: "Wiki",
    threadBadge: (count) => `Thread ${count}`,
    threadPosition: (position, total) => `${position}/${total}`,
    followStory: "Segui la notizia",
    followStoryLabel: (postLabel, threadLabel) =>
      `Segui la notizia: ${postLabel}, thread ${threadLabel}`,
    selectedPost: "TX selezionato",
    threadTimeline: (threadLabel) => `Timeline del thread ${threadLabel}`,
    threadTimelineFailed: "Impossibile caricare il thread. Riprova.",
    readFullThread: "Leggi tutto il thread",
    latestUpdate: "Ultimo aggiornamento",
    latestUpdateShort: "Aggiornato",
    telexPrompt: "Scrivi al sistema",
    newPosts: (count) => (count === 1 ? "1 nuovo TX" : `${count} nuovi TX`),
    olderPosts: "Messaggi precedenti",
    newerPosts: "Messaggi successivi",
    openPost: (postLabel) => `Apri ${postLabel}`,
    sharePost: "Condividi messaggio",
    share: "Condividi",
    copyText: "Copia testo",
    directLink: "Link diretto",
  },
  taxonomy: {
    tagEyebrow: "Tag",
    countryEyebrow: "Paese",
    tagTitle: (label) => `${label}: messaggi e aggiornamenti`,
    countryTitle: (label) => `${label}: notizie e aggiornamenti`,
    tagIntro: (label) =>
      `Tutti i messaggi di Silent People marcati con il tag ${label}: cronologia completa, aggiornamenti e thread collegati, dal piu recente al piu vecchio.`,
    countryIntro: (label) =>
      `Tutti i messaggi di Silent People sul paese ${label}: cronologia completa, aggiornamenti e thread collegati, dal piu recente al piu vecchio.`,
    backToFeed: "Torna al feed",
    otherTags: "Altri tag",
    otherCountries: "Altri paesi",
  },
  post: {
    permalinkThreadHint: "Questo permalink apre l'intero thread collegato.",
    currentlyViewedMessage: "Messaggio visualizzato",
    reportTimeline: "Timeline del TX",
    threadNumber: (postLabel) => `Thread ${postLabel}`,
    media: "Media",
    embeds: "Social",
    latestUpdate: "Ultimo aggiornamento",
    copy: "Copia",
    share: "Condividi",
    threadOfMessages: (count) => `Thread di ${count} messaggi`,
    singleMessage: "Messaggio singolo",
    postTitleFallback: "Messaggio",
    back: "Indietro",
    directLink: "Link diretto",
    linkShort: "Link",
    olderPost: "TX precedente",
    newerPost: "TX successivo",
    backToDispatches: "Torna ai dispacci",
    linkedCount: (count) => `${count} TX collegati`,
    otherDispatches: "Altri dispacci",
    otherDispatchesHint: "Nel flusso cronologico, anche fuori da questa notizia.",
    siteArrival: (postLabel) => `Nuovo ${postLabel}`,
    siteArrivals: (count, latestLabel) => `${count} nuovi TX · ultimo ${latestLabel}`,
    openSiteArrival: (postLabel) => `Apri ${postLabel}, appena uscito`,
    dismissSiteArrivals: "Chiudi l'avviso dei nuovi TX",
  },
  media: {
    adultOnlyTitle: "Contenuto per adulti",
    reveal: "Mostra",
    revealMedia: (index, total) => `Mostra media ${index} di ${total}`,
    openMedia: (index, total) => `Apri media ${index} di ${total}`,
    images: "Immagini",
    maps: "Mappe",
    videos: "Video",
    video: "Video",
    playVideo: "Riproduci video",
    expandVideo: "Ingrandisci video",
    audio: "Audio",
    closeViewer: "Chiudi visualizzatore media",
    closeMapViewer: "Chiudi visualizzatore mappa",
    previousMedia: "Media precedente",
    nextMedia: "Media successivo",
  },
  embed: {
    viewOnTikTok: "Apri su TikTok",
    viewPostOnX: "Apri post su X",
    viewOnProvider: (providerLabel) => `Apri su ${providerLabel}`,
  },
  share: {
    defaultTitle: "Messaggio SilentPeople",
    shared: "Condiviso",
    unavailable: "Condivisione non disponibile",
    linkCopied: "Link copiato",
    failed: "Condivisione non riuscita",
    nothingToCopy: "Nessun testo da copiare",
    textCopied: "Testo copiato",
    threadCopied: "Thread copiato",
  },
  push: {
    initializingTitle: "Inizializzazione notifiche",
    initializingMessage: "Sto verificando il supporto del browser per le notifiche push.",
    loadingOneSignalMessage: "Sto caricando OneSignal. Riprova tra un attimo.",
    unsupportedTitle: "Notifiche non disponibili",
    requiresHttps: "Le notifiche push richiedono HTTPS.",
    iosHomeScreenRequired:
      "Su iPhone e iPad le notifiche funzionano solo dopo aver aggiunto il sito alla Home Screen e averlo aperto da li.",
    missingServiceWorker:
      "Questo browser non espone le Service Worker richieste per le notifiche push.",
    missingNotificationApi:
      "Questo browser non espone l'API Notification richiesta per le notifiche push.",
    missingPushManager:
      "Questo browser non espone il PushManager richiesto per le notifiche push.",
    unsupportedGeneric: "Le notifiche non sono disponibili su questo dispositivo o browser.",
    initErrorTitle: "Errore inizializzazione notifiche",
    initErrorFallback: "OneSignal non e riuscito a inizializzarsi correttamente in questo browser.",
    onesignalUnsupported: "OneSignal segnala che questo browser non supporta il web push.",
    subscribedTitle: "Notifiche attive — clicca per disattivare",
    subscribedMessage: "Le notifiche push sono attive su questo browser.",
    permissionDeniedTitle: "Notifiche bloccate",
    permissionDeniedMessage:
      "Questo browser ha bloccato le notifiche per il sito. Riabilitale dalle impostazioni del sito e riprova.",
    enableTitle: "Attiva notifiche",
    permissionGrantedPendingMessage:
      "Le notifiche sono consentite dal browser ma non risultano ancora attivate in OneSignal.",
    clickToEnableMessage: "Clicca per attivare le notifiche push in questo browser.",
    alreadyActive: "Le notifiche sono gia attive su questo browser.",
    notReadyReload: "OneSignal non e pronto. Ricarica la pagina e riprova.",
    enabled: "Notifiche attivate.",
    disabled: "Notifiche disattivate.",
    invisiblePrompt:
      "Il browser non ha mostrato alcun popup visibile. Controlla i permessi del sito e riprova.",
    optInFailed: "La richiesta di attivazione notifiche non e riuscita.",
    optOutFailed: "La disattivazione delle notifiche non e riuscita.",
    menuLabel: "Notifiche",
    toggleOn: "ON",
    toggleOff: "OFF",
  },
  telex: {
    checkingLink: "Controllo link...",
    invalidLink: "Link non trovato.",
    redirecting: "Reindirizzamento...",
    sendTelex: "INVIA TELEX",
    logout: "Esci",
    attached: "Allegato",
    attachmentReady: (kind) =>
      kind === "image" ? "Immagine pronta" : kind === "video" ? "Video pronto" : "Nota vocale pronta",
    remove: "Rimuovi",
    composeDialogLabel: "Invia telex",
    composeEyebrow: "Invia telex",
    composeTitle: "Condividi un messaggio",
    composeDescription: "Testo prima, poi allega un'immagine, un video o una nota vocale.",
    close: "Chiudi",
    textPlaceholder: "Scrivi il tuo telex...",
    addAttachmentTitle: "Aggiungi foto, video o nota vocale",
    addAttachmentSubtitle: "Massimo un allegato",
    cancel: "Annulla",
    sending: "Invio in corso...",
    attachmentPickerLabel: "Scegli allegato telex",
    attachmentEyebrow: "Allega",
    attachmentTitle: "Aggiungi media al tuo telex",
    back: "Indietro",
    selectImage: "Seleziona immagine",
    selectImageHelp: "Scegli una foto e comprimila per il caricamento.",
    selectVideo: "Seleziona video",
    selectVideoHelp: "Allega una clip MP4 o WebM da questo dispositivo.",
    recordAudio: "Registra audio",
    recordAudioHelp: "Apri un registratore focalizzato per note vocali.",
    recordVideo: "Registra video",
    recordVideoHelp: "Avvia una vista fotocamera a schermo pieno.",
    audioDialogLabel: "Registra telex audio",
    cancelRecording: "Annulla registrazione",
    voiceTelex: "Telex vocale",
    finalizing: "Finalizzazione",
    paused: "In pausa",
    ready: "Pronto",
    recording: "Registrazione",
    maxDuration: (label) => `Max ${label}`,
    pauseRecording: "Metti in pausa registrazione",
    resumeRecording: "Riprendi registrazione",
    startRecording: "Avvia registrazione",
    stopRecording: "Ferma registrazione",
    videoDialogLabel: "Registra telex video",
    readyToRecord: "Pronto a registrare",
    frameShot: "Inquadra la scena, scegli una camera, poi avvia la registrazione.",
    recordingPaused: "Registrazione in pausa. Riprendi quando vuoi.",
    holdSteady: "Mantieni fermo il dispositivo e tieni il messaggio breve e chiaro.",
    switchCamera: "Cambia camera",
    chooseCameraBeforeStart: "Scegli la camera prima di iniziare",
    finalizingVideo: "Finalizzazione video...",
    sentDialogLabel: "Telex inviato",
    dismissConfirmation: "Chiudi conferma",
    sent: "Inviato",
    telexSent: "Telex inviato",
    sentDescription: "Il tuo messaggio e in arrivo. Questa schermata si chiude automaticamente.",
    backNow: "Torna ora",
    recordingLimitReached: (kind) =>
      kind === "audio"
        ? "La registrazione vocale ha raggiunto il limite di 2 minuti."
        : "La registrazione video ha raggiunto il limite di 2 minuti.",
    invalidSession: "La sessione del link non e valida.",
    cameraAndMicrophoneAccessFailed: "Impossibile accedere a camera e microfono.",
    requestedCameraUnavailable: "Impossibile accedere alla camera richiesta.",
    liveVideoUnavailable: "La registrazione video live non e disponibile qui.",
    switchCameraFailed: "Impossibile cambiare camera.",
    signUploadFailed: "Impossibile firmare il caricamento del media.",
    mediaUploadFailed: "Caricamento media non riuscito.",
    writeMessageOrAttachMedia: "Scrivi un messaggio o allega un media.",
    messageTooLong: (limit) => `Il messaggio supera ${limit} caratteri non URL.`,
    sendFailed: "Invio telex non riuscito.",
    imagesOnly: "Qui sono consentite solo immagini. Usa Registra video o Registra audio.",
    prepareImageFailed: "Impossibile preparare l'immagine.",
    unsupportedVideoFile: "Sono supportati solo file video MP4 e WebM.",
    videoTooLarge: (sizeMb) => `Il video supera ${sizeMb}MB.`,
    prepareVideoFailed: "Impossibile preparare il video.",
    audioRecordingUnsupported: "La registrazione audio non e supportata in questo browser.",
    initAudioRecorderFailed: "Impossibile inizializzare il registratore audio.",
    audioRecordingFailed: "Registrazione audio non riuscita.",
    recordedAudioEmpty: "L'audio registrato e vuoto.",
    recordedAudioUnsupported: "Il formato audio registrato non e supportato.",
    finalizeAudioFailed: "Impossibile finalizzare la registrazione audio.",
    microphoneAccessFailed: "Impossibile accedere al microfono.",
    liveVideoUnsupportedChooseVideo:
      "La registrazione video live non e supportata qui. Scegli invece un video.",
    liveVideoUnavailableChooseVideo:
      "La registrazione video live non e disponibile qui. Scegli invece un video.",
    videoRecordingFailed: "Registrazione video non riuscita.",
    recordedVideoEmpty: "Il video registrato e vuoto.",
    recordedVideoUnsupported: "Il formato video registrato non e supportato.",
    finalizeVideoFailed: "Impossibile finalizzare la registrazione video.",
  },
};

const ENGLISH_VOCABULARY: PublicVocabulary = {
  seo: {
    siteTitle: "War, crises and intelligence: the dispatches | SilentPeople",
    siteDescription:
      "Independent bulletin of the Non-Aligned Thought (PNA): short dispatches on war, crises, intelligence and economics, country by country, updated every day.",
    feedNotFoundTitle: "Not found",
    postNotFoundTitle: "Post not found",
  },
  status: {
    notFoundEyebrow: "error 404 / dead line",
    notFoundTitle: "Page not found",
    notFoundDescription:
      "The post or page you are looking for no longer exists, or the address was mistyped. Head back to the feed for the latest dispatches.",
    backToFeed: "Back to the feed",
    errorEyebrow: "error 500 / transmission interrupted",
    errorTitle: "Something broke",
    errorDescription:
      "We could not load this page. Try again — if it keeps failing, reopen the feed in a few minutes.",
    errorDigest: (digest) => `Error code ${digest}`,
    retry: "Try again",
    reload: "Reload the page",
    loadingEyebrow: "receiving",
    loadingTitle: "Loading dispatches...",
    loadingDescription: "Opening the line to the centre.",
  },
  feed: {
    browseEyebrow: "Browse",
    searchAndFilters: "Search, countries & filters",
    closeFilters: "Close filters",
    searchPosts: "Search posts",
    searchPlaceholder: "Text, story or TX",
    loading: "Loading...",
    loadFailed: "Unable to load posts right now. Try again.",
    loadMoreFailed: "Unable to load more posts right now.",
    updateFiltersFailed: "Unable to update filters right now.",
    search: "Search",
    clear: "Clear",
    threadsOnly: "Threads only",
    videosOnly: "Videos only",
    categories: "Countries",
    tags: "Tags",
    resetFilters: "Reset filters",
    backHome: "Back to home",
    filters: "Filters",
    menu: "Menu",
    close: "Close",
    emptyWithFilters: "No posts match the current search or filters.",
    emptyWithoutFilters: "No posts.",
    loadingMore: "Loading...",
    scrollForMore: "Scroll for more",
    comunicatoBadge: "PNA Statement",
    wikiBadge: "Wiki",
    threadBadge: (count) => `Thread ${count}`,
    threadPosition: (position, total) => `${position}/${total}`,
    followStory: "Follow the story",
    followStoryLabel: (postLabel, threadLabel) =>
      `Follow the story: ${postLabel}, thread ${threadLabel}`,
    selectedPost: "Selected TX",
    threadTimeline: (threadLabel) => `Thread ${threadLabel} timeline`,
    threadTimelineFailed: "Unable to load the thread. Try again.",
    readFullThread: "Read full thread",
    latestUpdate: "Latest update",
    latestUpdateShort: "Updated",
    telexPrompt: "Write to the system",
    newPosts: (count) => (count === 1 ? "1 new TX" : `${count} new TXs`),
    olderPosts: "Older posts",
    newerPosts: "Newer posts",
    openPost: (postLabel) => `Open ${postLabel}`,
    sharePost: "Share post",
    share: "Share",
    copyText: "Copy text",
    directLink: "Direct link",
  },
  taxonomy: {
    tagEyebrow: "Tag",
    countryEyebrow: "Country",
    tagTitle: (label) => `${label}: posts and updates`,
    countryTitle: (label) => `${label}: news and updates`,
    tagIntro: (label) =>
      `Every Silent People post tagged ${label}: the full timeline, updates and connected threads, newest first.`,
    countryIntro: (label) =>
      `Every Silent People post about ${label}: the full timeline, updates and connected threads, newest first.`,
    backToFeed: "Back to the feed",
    otherTags: "More tags",
    otherCountries: "More countries",
  },
  post: {
    permalinkThreadHint: "This permalink opens the full connected thread.",
    currentlyViewedMessage: "Currently viewed message",
    reportTimeline: "Timeline del TX",
    threadNumber: (postLabel) => `Thread ${postLabel}`,
    media: "Media",
    embeds: "Social",
    latestUpdate: "Latest update",
    copy: "Copy",
    share: "Share",
    threadOfMessages: (count) => `Thread of ${count} messages`,
    singleMessage: "Single message",
    postTitleFallback: "Post",
    back: "Back",
    directLink: "Direct link",
    linkShort: "Link",
    olderPost: "Previous TX",
    newerPost: "Next TX",
    backToDispatches: "Back to dispatches",
    linkedCount: (count) => `${count} linked TX`,
    otherDispatches: "Other dispatches",
    otherDispatchesHint: "In the chronological feed, possibly outside this story.",
    siteArrival: (postLabel) => `New ${postLabel}`,
    siteArrivals: (count, latestLabel) => `${count} new TXs · latest ${latestLabel}`,
    openSiteArrival: (postLabel) => `Open ${postLabel}, just out`,
    dismissSiteArrivals: "Dismiss the new TX notice",
  },
  media: {
    adultOnlyTitle: "Adult-only content",
    reveal: "Reveal",
    revealMedia: (index, total) => `Reveal media ${index} of ${total}`,
    openMedia: (index, total) => `Open media ${index} of ${total}`,
    images: "Images",
    maps: "Maps",
    videos: "Videos",
    video: "Video",
    playVideo: "Play video",
    expandVideo: "Expand video",
    audio: "Audio",
    closeViewer: "Close media viewer",
    closeMapViewer: "Close map viewer",
    previousMedia: "Previous media",
    nextMedia: "Next media",
  },
  embed: {
    viewOnTikTok: "View on TikTok",
    viewPostOnX: "View post on X",
    viewOnProvider: (providerLabel) => `View on ${providerLabel}`,
  },
  share: {
    defaultTitle: "SilentPeople post",
    shared: "Shared",
    unavailable: "Sharing unavailable",
    linkCopied: "Link copied",
    failed: "Share failed",
    nothingToCopy: "Nothing to copy",
    textCopied: "Text copied",
    threadCopied: "Thread copied",
  },
  push: {
    initializingTitle: "Initializing notifications",
    initializingMessage: "Checking browser support for push notifications.",
    loadingOneSignalMessage: "Loading OneSignal. Try again in a moment.",
    unsupportedTitle: "Notifications unavailable",
    requiresHttps: "Push notifications require HTTPS.",
    iosHomeScreenRequired:
      "On iPhone and iPad, notifications only work after adding the site to the Home Screen and opening it from there.",
    missingServiceWorker:
      "This browser does not expose the Service Worker support required for push notifications.",
    missingNotificationApi:
      "This browser does not expose the Notification API required for push notifications.",
    missingPushManager:
      "This browser does not expose the PushManager required for push notifications.",
    unsupportedGeneric: "Notifications are not available on this device or browser.",
    initErrorTitle: "Notification initialization error",
    initErrorFallback: "OneSignal could not initialize correctly in this browser.",
    onesignalUnsupported: "OneSignal reports that this browser does not support web push.",
    subscribedTitle: "Notifications enabled — click to disable",
    subscribedMessage: "Push notifications are enabled in this browser.",
    permissionDeniedTitle: "Notifications blocked",
    permissionDeniedMessage:
      "This browser has blocked notifications for the site. Re-enable them in the site settings and try again.",
    enableTitle: "Enable notifications",
    permissionGrantedPendingMessage:
      "Notifications are allowed by the browser but are not yet enabled in OneSignal.",
    clickToEnableMessage: "Click to enable push notifications in this browser.",
    alreadyActive: "Notifications are already enabled in this browser.",
    notReadyReload: "OneSignal is not ready. Reload the page and try again.",
    enabled: "Notifications enabled.",
    disabled: "Notifications disabled.",
    invisiblePrompt:
      "The browser did not show a visible prompt. Check the site permissions and try again.",
    optInFailed: "The notification opt-in request failed.",
    optOutFailed: "The notification opt-out request failed.",
    menuLabel: "Notifications",
    toggleOn: "ON",
    toggleOff: "OFF",
  },
  telex: {
    checkingLink: "Checking link...",
    invalidLink: "Link not found.",
    redirecting: "Redirecting...",
    sendTelex: "SEND TELEX",
    logout: "Logout",
    attached: "Attached",
    attachmentReady: (kind) =>
      kind === "image" ? "Image ready" : kind === "video" ? "Video ready" : "Voice note ready",
    remove: "Remove",
    composeDialogLabel: "Send telex",
    composeEyebrow: "Send telex",
    composeTitle: "Share a message",
    composeDescription: "Text first, then attach one image, video, or voice note.",
    close: "Close",
    textPlaceholder: "Write your telex...",
    addAttachmentTitle: "Add photo, video, or voice note",
    addAttachmentSubtitle: "One attachment max",
    cancel: "Cancel",
    sending: "Sending...",
    attachmentPickerLabel: "Choose telex attachment",
    attachmentEyebrow: "Attach",
    attachmentTitle: "Add media to your telex",
    back: "Back",
    selectImage: "Select image",
    selectImageHelp: "Choose one photo and compress it for upload.",
    selectVideo: "Select video",
    selectVideoHelp: "Attach an MP4 or WebM clip from this device.",
    recordAudio: "Record audio",
    recordAudioHelp: "Open a focused voice-note recorder.",
    recordVideo: "Record video",
    recordVideoHelp: "Launch a full-screen camera capture view.",
    audioDialogLabel: "Record audio telex",
    cancelRecording: "Cancel recording",
    voiceTelex: "Voice telex",
    finalizing: "Finalizing",
    paused: "Paused",
    ready: "Ready",
    recording: "Recording",
    maxDuration: (label) => `Max ${label}`,
    pauseRecording: "Pause recording",
    resumeRecording: "Resume recording",
    startRecording: "Start recording",
    stopRecording: "Stop recording",
    videoDialogLabel: "Record video telex",
    readyToRecord: "Ready to record",
    frameShot: "Frame your shot, choose a camera, then start recording.",
    recordingPaused: "Recording paused. Resume when you are ready.",
    holdSteady: "Hold steady and keep the message short and clear.",
    switchCamera: "Switch camera",
    chooseCameraBeforeStart: "Choose camera before recording starts",
    finalizingVideo: "Finalizing video...",
    sentDialogLabel: "Telex sent",
    dismissConfirmation: "Dismiss confirmation",
    sent: "Sent",
    telexSent: "Telex sent",
    sentDescription: "Your message is on its way. This screen closes automatically.",
    backNow: "Back now",
    recordingLimitReached: (kind) =>
      kind === "audio"
        ? "Voice recording reached the 2 minute limit."
        : "Video recording reached the 2 minute limit.",
    invalidSession: "Link session is invalid.",
    cameraAndMicrophoneAccessFailed: "Could not access camera and microphone.",
    requestedCameraUnavailable: "Could not access the requested camera.",
    liveVideoUnavailable: "Live video recording is unavailable here.",
    switchCameraFailed: "Could not switch camera.",
    signUploadFailed: "Could not sign media upload.",
    mediaUploadFailed: "Media upload failed.",
    writeMessageOrAttachMedia: "Write a message or attach media.",
    messageTooLong: (limit) => `Message exceeds ${limit} non-URL characters.`,
    sendFailed: "Could not send telex.",
    imagesOnly: "Only images are allowed here. Use Record video or Record audio.",
    prepareImageFailed: "Could not prepare image.",
    unsupportedVideoFile: "Only MP4 and WebM video files are supported.",
    videoTooLarge: (sizeMb) => `Video exceeds ${sizeMb}MB.`,
    prepareVideoFailed: "Could not prepare video.",
    audioRecordingUnsupported: "Audio recording is not supported in this browser.",
    initAudioRecorderFailed: "Could not initialize audio recorder.",
    audioRecordingFailed: "Audio recording failed.",
    recordedAudioEmpty: "Recorded audio is empty.",
    recordedAudioUnsupported: "Recorded audio format is not supported.",
    finalizeAudioFailed: "Could not finalize audio recording.",
    microphoneAccessFailed: "Could not access microphone.",
    liveVideoUnsupportedChooseVideo:
      "Live video recording is not supported here. Choose a video instead.",
    liveVideoUnavailableChooseVideo:
      "Live video recording is unavailable here. Choose a video instead.",
    videoRecordingFailed: "Video recording failed.",
    recordedVideoEmpty: "Recorded video is empty.",
    recordedVideoUnsupported: "Recorded video format is not supported.",
    finalizeVideoFailed: "Could not finalize video recording.",
  },
};

export function getPublicVocabulary(language: PostLanguage): PublicVocabulary {
  return language === "en" ? ENGLISH_VOCABULARY : ITALIAN_VOCABULARY;
}

export function getLocalizedSiteTitle(language: PostLanguage): string {
  return getPublicVocabulary(language).seo.siteTitle;
}

export function getLocalizedSiteDescription(language: PostLanguage): string {
  return getPublicVocabulary(language).seo.siteDescription;
}
